import { Stack } from '@mui/material';
import {
  ScreenWrap,
  HeaderBreadcrumbs,
  AppointmentsCalendar,
  SoloLoading,
  ScreenError,
} from '../../../components';
import { useNavigate } from 'react-router';
import { Api, useApiCallable } from '../../../api';
import { toastErrorHandler } from '../../../util';
import useModifiableParams from '../../../hooks/use-modifiable-params';
import {
  format,
  addDays,
  startOfWeek,
  endOfWeek,
  add,
  parseISO,
} from 'date-fns';
import { Model } from '../../../models';
import { toast } from 'sonner';

export type Breadcrumb = { name: string; url: string };

interface AppointmentsViewProps {
  clinicianId: string;
  timezone: string;
  bufferMinutes: number;
  title?: string;
  breadcrumbs?: Breadcrumb[];
  showEditButton?: boolean;
  editButtonText?: string;
  onEditClick?: () => void;
}

export default function AppointmentsView({
  clinicianId,
  timezone,
  bufferMinutes,
  title,
  breadcrumbs,
  showEditButton = true,
  editButtonText,
  onEditClick,
}: AppointmentsViewProps) {
  const navigate = useNavigate();
  const callApi = useApiCallable();
  const { getParams, setModifiedParams } = useModifiableParams();

  const params = getParams();
  const startDate =
    params.startDate ||
    format(startOfWeek(new Date(), { weekStartsOn: 1 }), 'yyyy-MM-dd');
  const endDate =
    params.endDate ||
    format(endOfWeek(new Date(), { weekStartsOn: 1 }), 'yyyy-MM-dd');

  const weeklyAvailabilityApi =
    Api.ClinicianWeeklyAvailability.useReadCollectionWeeklyAvailabilities(
      clinicianId,
    );
  const dateAvailabilityApi =
    Api.ClinicianDateAvailability.useReadCollectionDateAvailabilities(
      clinicianId,
    );
  const appointmentsApi =
    Api.Appointment.useReadCollectionAppointmentsStartDateEndDate(
      clinicianId,
      startDate,
      endDate,
    );

  if (weeklyAvailabilityApi.error)
    return <ScreenError {...weeklyAvailabilityApi} />;
  if (dateAvailabilityApi.error)
    return <ScreenError {...dateAvailabilityApi} />;
  if (appointmentsApi.error) return <ScreenError {...appointmentsApi} />;
  if (
    !weeklyAvailabilityApi.data ||
    !dateAvailabilityApi.data ||
    !appointmentsApi.data
  ) {
    return <SoloLoading />;
  }

  const handlePreviousWeek = () => {
    setModifiedParams({
      startDate: format(addDays(new Date(startDate), -7), 'yyyy-MM-dd'),
      endDate: format(addDays(new Date(endDate), -7), 'yyyy-MM-dd'),
    });
  };

  const handleNextWeek = () => {
    setModifiedParams({
      startDate: format(addDays(new Date(startDate), 7), 'yyyy-MM-dd'),
      endDate: format(addDays(new Date(endDate), 7), 'yyyy-MM-dd'),
    });
  };

  const handleToday = () => {
    setModifiedParams({
      startDate: null,
      endDate: null,
    });
  };

  const timeToMinutes = (time: string) => {
    const [h, m] = time.split(':').map(Number);
    return h * 60 + m;
  };

  const days = (() => {
    const list: Array<{ name: string; key: string; date: string }> = [];
    const start = new Date(startDate);
    const end = new Date(endDate);
    for (let d = new Date(start); d <= end; d = add(d, { days: 1 })) {
      list.push({
        name: `${format(d, 'EEE').toUpperCase()} ${format(d, 'd')}`,
        key: format(d, 'eee').toLowerCase(),
        date: format(d, 'yyyy-MM-dd'),
      });
    }
    return list;
  })();

  const availableHours: { [date: string]: { start: string; end: string }[] } =
    (() => {
      const map: { [date: string]: { start: string; end: string }[] } = {};
      const weekly = weeklyAvailabilityApi.data?.member ?? [];
      const dateA = dateAvailabilityApi.data?.member ?? [];

      days.forEach((d) => {
        const weekday = parseISO(d.date).getDay();
        const weeklyAvail = weekly.find((w) => w.weekday === weekday);
        if (weeklyAvail?.windows?.length) {
          map[d.date] = weeklyAvail.windows.map((w) => ({
            start: w.startTime,
            end: w.endTime,
          }));
        }
      });

      dateA.forEach((da) => {
        if (da.date) {
          map[da.date] = da.windows.map((w) => ({
            start: w.startTime,
            end: w.endTime,
          }));
        }
      });

      return map;
    })();

  const generateHours = () => {
    const points: number[] = [];
    Object.values(availableHours).forEach((slots) => {
      slots.forEach((s) => {
        points.push(
          Model.Appointment.timeToMinutes(s.start),
          Model.Appointment.timeToMinutes(s.end),
        );
      });
    });
    if (!points.length) {
      return [
        '08:00',
        '09:00',
        '10:00',
        '11:00',
        '12:00',
        '13:00',
        '14:00',
        '15:00',
        '16:00',
        '17:00',
        '18:00',
      ];
    }
    const min = Math.min(...points);
    const max = Math.max(...points);
    const startHour = Math.max(0, Math.floor((min - 60) / 60));
    const endHour = Math.min(23, Math.floor((max + 60) / 60));
    const arr: string[] = [];
    for (let h = startHour; h <= endHour; h++)
      arr.push(`${h.toString().padStart(2, '0')}:00`);
    return arr;
  };

  const hours = generateHours();

  const handleMarkUnavailable = async (dateISO: string, timeSlot: string) => {
    try {
      const cleaned = timeSlot.replace(/\s/g, '');
      const [startStr, endStr] = cleaned.split('-');
      const blockStart = Model.Appointment.timeToMinutes(startStr);
      const blockEnd = Model.Appointment.timeToMinutes(endStr);

      const dayWindows = availableHours[dateISO] ?? [];
      const nextWindows: Array<{ startTime: string; endTime: string }> = [];

      dayWindows.forEach(({ start, end }) => {
        const ws = timeToMinutes(start);
        const we = timeToMinutes(end);

        if (blockEnd <= ws || blockStart >= we) {
          nextWindows.push({ startTime: start, endTime: end });
          return;
        }

        if (ws < blockStart) {
          nextWindows.push({
            startTime: start,
            endTime: Model.Appointment.minutesToTime(blockStart),
          });
        }
        if (blockEnd < we) {
          nextWindows.push({
            startTime: Model.Appointment.minutesToTime(blockEnd),
            endTime: end,
          });
        }
      });

      const existingSpecificDates = dateAvailabilityApi.data?.member ?? [];
      const mergedDays = existingSpecificDates
        .filter((d) => d.date !== dateISO)
        .map((d) => ({
          date: d.date,
          windows: d.windows.map((w) => ({
            startTime: w.startTime,
            endTime: w.endTime,
          })),
        }));

      mergedDays.push({
        date: dateISO,
        windows: nextWindows,
      });

      const payload: Api.ClinicianDateAvailability.W = {
        days: mergedDays,
      };

      const response = await callApi(
        `/clinicians/${clinicianId}/date-availabilities`,
        { method: 'POST', jsonBody: payload },
        { errorHandler: toastErrorHandler },
      );
      if (!response) return;

      toast.success('Time block marked as unavailable');
      weeklyAvailabilityApi.invalidate?.();
      dateAvailabilityApi.invalidate?.();
      appointmentsApi.invalidate?.();
    } catch (error) {
      console.error('Mark unavailable error:', error);
      toast.error('Failed to mark time block unavailable');
    }
  };

  const formatWeekTitle = (start: string, end: string) => {
    const startMonth = format(new Date(start), 'MMMM');
    const endMonth = format(new Date(end), 'MMMM');
    const year = format(new Date(start), 'yyyy');

    if (startMonth === endMonth) {
      return `${startMonth} ${year}`;
    }
    return `${startMonth} - ${endMonth} ${year}`;
  };

  const computedTitle = title ?? `Appointments`;

  const appointments = appointmentsApi.data?.member ?? [];

  const onRescheduled = () => {
    appointmentsApi.invalidate?.();
  };

  return (
    <ScreenWrap>
      <Stack spacing={3} alignItems="stretch">
        <Stack gap={1}>
          <HeaderBreadcrumbs title={computedTitle} items={breadcrumbs ?? []} />
        </Stack>

        <AppointmentsCalendar
          title={formatWeekTitle(startDate, endDate)}
          showEditButton={showEditButton}
          editButtonText={editButtonText}
          onEditClick={
            onEditClick ||
            (() => navigate(`/clinicians/${clinicianId}/edit-schedule`))
          }
          clinicianId={clinicianId}
          timezone={timezone}
          days={days}
          hours={hours}
          availableHours={availableHours}
          appointments={appointments}
          bufferMinutes={bufferMinutes}
          onRescheduled={onRescheduled}
          onPreviousWeek={handlePreviousWeek}
          onNextWeek={handleNextWeek}
          onToday={handleToday}
          onMarkUnavailable={handleMarkUnavailable}
        />
      </Stack>
    </ScreenWrap>
  );
}
